import React from 'react'

import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  Display,
  FlexDirection,
  Heading5,
  Text,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { SearchStandardRecipeItem } from '@library/api-menu-experience'
import { MenuSearchRecipe, type MenuSearchRecipeProps } from '@library/menu-page'

import {
  getBasketDate,
  getBasketMenuUuid,
  getBasketMenuUrn,
  getBasketRecipes,
  getNumPortions,
} from 'selectors/basket'
import { getBasketRecipeNumPortions } from 'utils/basket'

import { useRecipeTile } from '../../../MenuExperience/MenuExperiencePage/hooks/useRecipeTile'

/** Search shares refreshed data, while retaining its own identity and search metadata. */
export const SearchRecipeData = ({
  recipe,
  children,
}: {
  recipe: SearchStandardRecipeItem
  children: (recipe: SearchStandardRecipeItem) => React.ReactNode
}) => {
  const menuId = useSelector(getBasketMenuUuid)
  const menuUrn = useSelector(getBasketMenuUrn)
  const deliveryDate = useSelector(getBasketDate)
  const basketRecipes = useSelector(getBasketRecipes)
  const boxNumPortions = useSelector(getNumPortions)
  const basketRecipe = basketRecipes?.get(recipe.id)
  const numPortions = getBasketRecipeNumPortions(basketRecipe, boxNumPortions)
  const { tile, error, isValidating, mutate } = useRecipeTile(
    { menuId, menuUrn, deliveryDate, recipeId: recipe.id, recipeUrn: recipe.urn, numPortions },
    {
      ...recipe,
      isAvailable: recipe.isAvailable ?? true,
      portions: recipe.portions?.map((portion) => ({
        ...portion,
        isAvailable: portion.isAvailable ?? true,
      })),
    },
    recipe.numPortions ?? boxNumPortions,
  )

  if (!tile) {
    return (
      <Box
        alignItems={AlignItems.Center}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={3}
        paddingH={4}
        paddingV={6}
      >
        <Heading5>{recipe.title}</Heading5>
        {error && !isValidating ? (
          <>
            <div role="alert">
              <Text>We couldn&apos;t load information for this recipe.</Text>
            </div>
            <Button
              colorVariant={ButtonColorVariant.Tertiary}
              onClick={() => void mutate().catch(() => undefined)}
              type="button"
            >
              Try again
            </Button>
          </>
        ) : (
          <div role="status">
            <Text>Loading recipe information…</Text>
          </div>
        )}
      </Box>
    )
  }

  const resolved = { ...recipe, ...tile }

  return recipe.base ? (
    <SearchRecipeData recipe={recipe.base}>
      {(base) => children({ ...resolved, base })}
    </SearchRecipeData>
  ) : (
    <>{children(resolved)}</>
  )
}

export const SearchRecipe = ({ recipe, ...props }: MenuSearchRecipeProps) => (
  <SearchRecipeData recipe={recipe}>
    {(resolved) => (
      // Keep the search callbacks and tracking context while replacing only recipe data.
      // eslint-disable-next-line react/jsx-props-no-spreading
      <MenuSearchRecipe {...props} recipe={resolved} />
    )}
  </SearchRecipeData>
)
